
import { Category, GetGradeById, GetGradeResponse, GetTermById, GetTermResponse, Grade, ResponseStatus, Term, Version } from '@/api/ctcae.dtos';
import DefaultClient from '@ngt/forms/src/api/DefaultClient';
import { JsonServiceClient } from '@servicestack/client';
import { type } from 'os';
import { useMemo, useEffect, useState } from 'react';
import { SWRConfiguration, SWRResponse, useSWRConfig } from 'swr';
import { serialize } from 'swr/_internal';

export interface ICtcaeIdentifier {
    termId?: number;
    gradeId?: number;
    specification?: string;
    staticTerm?: boolean;
}

export interface ICtcae {
    version: Version;
    category: Category;
    parentTerm?: Term;
    term: Term;
    grade?: Grade;
    specification?: string;
}

export interface ICtcaeCache {
    data?: ICtcae;
    error?: ResponseStatus;
    isLoading?: boolean;
}

export enum CtcaeType {
    Term = 1,
    Grade = 2,
}

export interface ICtcaeListener {
    (ctcae: ICtcaeCache): void;
}

const initialData: ICtcaeCache = {
    isLoading: true,
    data: undefined,
    error: undefined
}

const defaultError: ICtcaeCache = {
    isLoading: false,
    data: undefined,
    error: new ResponseStatus({
        errorCode: "Fail",
        message: "The CTCAE data could not be loaded."
    })
}

class CtcaeCache {
    listeners: Record<CtcaeType, Record<number, ICtcaeListener[]>>;
    data: Record<CtcaeType, Record<number, ICtcaeCache>>;
    ctcaeClient: JsonServiceClient;
    
    constructor() {
        this.listeners = {
            [CtcaeType.Term]: {

            },
            [CtcaeType.Grade]: {

            }
        };

        this.data = {
            [CtcaeType.Term]: {

            },
            [CtcaeType.Grade]: {

            }
        };

        this.ctcaeClient = DefaultClient(import.meta.env.VITE_API_CTCAE_URL, '/api');
        this.ctcaeClient.credentials = "omit";
    }

    public loadCtcae = async (type: CtcaeType, key: number) => {
        if (this.data[type][key]) {
            return;
        }

        this.data[type][key] = {...initialData};

        if (type === CtcaeType.Term) {
            try {
                const response = await this.ctcaeClient.get<GetTermResponse>(`/json/reply/GetTermById?id=${key}&format=json`);
                
                this.data[type][key] = {
                    isLoading: false,
                    error: undefined,
                    data: response
                };
            }
            catch (error: any) {
                if (error.responseStatus) {
                    this.data[type][key] = {...defaultError, error: error.responseStatus};
                }
                else {
                    this.data[type][key] = {...defaultError };
                }

            }
        }
        else {
            try {
                const response = await this.ctcaeClient.get<GetGradeResponse>(`/json/reply/GetGradeById?id=${key}&format=json`);

                this.data[type][key] = {
                    isLoading: false,
                    error: undefined,
                    data: response
                };
            }
            catch (error: any) {
                if (error.responseStatus) {
                    this.data[type][key] = {...defaultError, error: error.responseStatus};
                }
                else {
                    this.data[type][key] = {...defaultError };
                }

            }
        }

        this.callListeners(type, key);
    }

    public getCtcae = (type: CtcaeType, key: number) => {
        return this.data[type][key] ?? {...initialData};
    }

    private callListeners = (type: CtcaeType, key: number) => {
        for (let listener of (this.listeners[type][key] ?? [])) 
        {
            listener(this.data[type][key] ?? {...initialData})
        }
    }

    public subscribe = (type: CtcaeType, key: number, listener: ICtcaeListener) => {
        if (!this.listeners[type][key]) {
            this.listeners[type][key] = [];
        }

        this.listeners[type][key].push(listener);

        return () => {
            this.listeners[type][key] = this.listeners[type][key].filter(x => x === listener);
        }
    }
}


export const GlobalCtcaeCache: CtcaeCache = new CtcaeCache();

export const getCtcae = async (data?: ICtcaeIdentifier) => {
    const type =  data?.gradeId ? CtcaeType.Grade : CtcaeType.Term;
    const key = type === CtcaeType.Grade ? data?.gradeId : data?.termId;

    if (!key) {
        return undefined;
    }

    await GlobalCtcaeCache.loadCtcae(type, key);

    
    const state = GlobalCtcaeCache.getCtcae(type, key);

    return state.data;
}

export const useCtcae = (data?: ICtcaeIdentifier) => {
    const [ctcae, setCtcae] = useState<ICtcae>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ResponseStatus>();
    
    useEffect(() => {
        const type =  data?.gradeId ? CtcaeType.Grade : CtcaeType.Term;

        const key = type === CtcaeType.Grade ? data?.gradeId : data?.termId;

        if (!key) {
            setCtcae(undefined);
            setError(undefined);
            setLoading(false);
            return;
        } 


        const initialState = GlobalCtcaeCache.getCtcae(type, key);

        setCtcae(initialState.data);
        setError(initialState.error);
        setLoading(!!initialState.isLoading);

        const unsubscribe = GlobalCtcaeCache.subscribe(type, key, (update) => {
            setCtcae(update.data);
            setError(update.error);
            setLoading(!!update.isLoading);
        })

        GlobalCtcaeCache.loadCtcae(type, key);

        return () => {
            unsubscribe();
        }
    }, [setCtcae, setLoading, setError, data?.gradeId, data?.termId, data?.staticTerm, data?.specification])

    const result = useMemo(() => {
        return {
            data: ctcae,
            error: error,
            loading: loading,
            validating: false
        }
    }, [ctcae, loading, error])

    return result;
};

export default useCtcae;